import { Suspense, forwardRef, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { CreateOrEditSurveyForm } from "../components/CreateUpdateSurveyForm"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { MERCHANT_ADMIN_ROUTE, MERCHANT_ADMIN_SURVEYS } from "../../../../../constants/routes"
import { createSurvey, SurveyQuery } from "../surveysListApi"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { CreateOrEditSurveyFormValues, SurveyTypeOptions } from "../merchantSurveyTypes"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { useMerchant } from "../../../../merchants/merchantHooks"

const Survey = forwardRef<FormikProps<CreateOrEditSurveyFormValues>>((_, ref) => {
    const { merchantId } = useMerchant()
    const navigate = useNavigate()

    const { mutate: callCreateSurvey } = useApiMutation(createSurvey, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SurveyQuery.LIST] })
            if (data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data.serviceResponse.displayMessage,
                })
            }
            navigate(MERCHANT_ADMIN_SURVEYS)
        },
    })

    return (
        <CreateOrEditSurveyForm
            ref={ref}
            onSubmit={(data) => {
                callCreateSurvey({
                    ...data,
                    type: SurveyTypeOptions.SMILEY,
                    merchant: {
                        id: merchantId,
                    },
                })
            }}
        />
    )
})

export function CreateSurvey() {
    const surveyFormRef = useRef<FormikProps<CreateOrEditSurveyFormValues>>(null)

    const handleSaveClick = () => {
        surveyFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="create-survey-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SURVEYS_LIST_CREATE}
                    title="Surveys"
                    rightElement={
                        <LocalizedStrict id="create-merchant-survey-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Create survey
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-surveys">
                            <BreadcrumbLink to={MERCHANT_ADMIN_SURVEYS}>Surveys</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="nav-item-create-survey-header">
                            <Breadcrumb>Create survey</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Survey ref={surveyFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
